// ═══════════════════════════════════════════════════════════════════════════════
//  AssessmentIntro — "Mide tu condición" + "¿Cómo funciona?"
//
//  Antes de medir hay que explicar qué se mide y qué NO. Es el mismo principio
//  de honestidad del onboarding: nombrar los límites del instrumento antes de
//  que el alumno los descubra solo. Un examen que se presenta como más de lo
//  que es destruye la confianza en todo el resto del producto.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';

const SKILLS = [
  { icon: '🧩', name: 'Use of English', text: 'Estructura, tiempos verbales y precisión gramatical.' },
  { icon: '📖', name: 'Reading', text: 'Comprensión literal, inferencia y lectura de subtexto.' },
  { icon: '🎧', name: 'Listening', text: 'Comprensión auditiva, detalle, tono e intención.' },
  { icon: '🗣️', name: 'Speaking', text: 'Hablas 30-45 segundos. Medimos riqueza, coherencia y fluidez.' },
  { icon: '✍️', name: 'Writing', text: 'Escribes entre 15 y 70 palabras. Nada de cartas largas.' },
];

const HOW = [
  {
    title: 'Se adapta a ti mientras avanzas',
    // Cifras medidas contra el simulador del motor, no estimadas: 15 reactivos
    // para un C1, 21 para un B1. A ~25 s por reactivo son 6-9 minutos.
    text: 'Si aciertas, sube de nivel. Si fallas, baja. Nadie contesta 60 preguntas: el examen se detiene en cuanto encuentra tu techo real. Son entre 15 y 21 preguntas — unos 6 a 10 minutos.',
  },
  {
    title: 'No te castiga',
    text: 'En cuanto queda claro que un nivel te queda grande, deja de insistir. No verás pregunta tras pregunta muy por encima de ti solo para llenar un cupo.',
  },
  {
    title: 'Mide competencias, no solo aciertos',
    text: 'Cada pregunta está etiquetada con lo que realmente mide. Por eso el resultado no es una nota: es un mapa de en qué eres sólido y dónde se te rompe.',
  },
  {
    title: 'Hablar y escribir: tareas cortas',
    text: 'Nada de redacciones de 200 palabras. En Writing escribes entre 15 y 70 palabras según tu nivel; en Speaking hablas menos de un minuto. Lo justo para mostrar cómo manejas el idioma.',
  },
  {
    title: 'Puedes interrumpirlo',
    text: 'Si cierras a la mitad, al volver continúas exactamente donde ibas. Tus respuestas se guardan en el momento en que las das.',
  },
];

export default function AssessmentIntro({ onStart, resuming, answered, canViewResults }) {
  const [showHow, setShowHow] = useState(false);

  return (
    <div className="as-screen">
      <p className="as-kicker">Evaluación de nivel</p>
      <h1 className="as-title">Midamos tu condición en el inglés</h1>

      <p className="as-lead">
        No para ponerte una etiqueta, sino para dejar de entrenarte a ciegas.
        Vamos a ver qué dominas y qué se te rompe en cada nivel — y con eso
        armamos tu entrenamiento.
      </p>

      {/* Honestidad sobre el instrumento, antes de empezar */}
      <p className="as-honesty">
        Esto no es un examen Cambridge oficial ni entrega un certificado — eso
        requiere una sesión acreditada. Lo que sí hace es ubicarte por nivel en
        cada habilidad con mucha más precisión que una prueba de 6 preguntas, y
        decirte exactamente en qué fallas.
        <br /><br />
        <strong>Sobre el Speaking:</strong> medimos qué dices y cómo lo
        construyes — vocabulario, gramática, coherencia y fluidez. No medimos
        pronunciación: el reconocimiento de voz del navegador normaliza el
        acento, y decir que la evaluamos sería mentirte.
      </p>

      <div className="as-skills">
        {SKILLS.map((s) => (
          <div key={s.name} className={`as-skill${s.soon ? ' as-skill-soon' : ''}`}>
            <span className="as-skill-icon" aria-hidden="true">{s.icon}</span>
            <div>
              <h2 className="as-skill-name">
                {s.name}
                {s.soon && <span className="as-soon">Próximamente</span>}
              </h2>
              <p className="as-skill-text">{s.text}</p>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="as-link"
        onClick={() => setShowHow((v) => !v)}
        aria-expanded={showHow}
      >
        {showHow ? '− Ocultar' : '+ ¿Cómo funciona?'}
      </button>

      {showHow && (
        <div className="as-how">
          {HOW.map((h) => (
            <div key={h.title} className="as-how-item">
              <h3>{h.title}</h3>
              <p>{h.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Reglas del muro de pago, dichas ANTES de invertir 15 minutos */}
      {!canViewResults && (
        <p className="as-notice">
          <strong>Antes de empezar:</strong> presentar la evaluación es parte de
          tu plan actual. Ver la radiografía completa requiere un plan de pago.
          Tus respuestas se guardan igual — si más adelante cambias de plan,
          desbloqueas tu resultado sin repetir el examen.
        </p>
      )}

      <div className="as-actions">
        <button type="button" className="as-btn as-btn-lg" onClick={onStart}>
          {resuming ? `Continuar donde iba (${answered} respondidas) →` : 'Empezar la evaluación →'}
        </button>
      </div>
    </div>
  );
}
